"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DCRFClient = exports.StreamingRequestPromise = void 0;
const autobind_decorator_1 = __importDefault(require("autobind-decorator"));
const lodash_uniqby_1 = __importDefault(require("lodash.uniqby"));
const logging_1 = require("./logging");
const fifo_1 = __importDefault(require("./dispatchers/fifo"));
const UUID_1 = __importDefault(require("./lib/UUID"));
const fifo_2 = __importDefault(require("./send_queues/fifo"));
const json_1 = __importDefault(require("./serializers/json"));
const subscriptions_1 = require("./subscriptions");
const websocket_1 = __importDefault(require("./transports/websocket"));
const log = logging_1.getLogger('dcrf');
/**
 * Promise representing the listening for responses during a streaming request, and offering an
 * cancel() method to stop listening for additional responses.
 *
 * This is returned from DCRFClient.streamingRequest.
 */
class StreamingRequestPromise extends Promise {
    constructor(executor, dispatcher, listenerId) {
        super(executor);
        this._dispatcher = dispatcher;
        this._listenerId = listenerId;
    }
    get listenerId() {
        return this._listenerId;
    }
    /**
     * Stop listening for new events on this subscription
     * @return true if the subscription was active, false if it was already unsubscribed
     */
    cancel() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._listenerId !== null) {
                const returnValue = this._dispatcher.cancel(this._listenerId);
                this._listenerId = null;
                return returnValue;
            }
            return false;
        });
    }
}
exports.StreamingRequestPromise = StreamingRequestPromise;
class DCRFClient {
    /**
     * @param dispatcher Dispatcher instance to route incoming frames to associated handlers
     * @param transport Transport to send and receive messages over the wire.
     * @param queue Instance of Queue to queue messages when transport unavailable.
     * @param serializer Instance which handles serializing data to be sent, and
     *                   deserializing received data.
     * @param options Configuration to customize how DCRFClient operates. See
     *                the IDCRFOptions type for more information.
     */
    constructor(dispatcher, transport, queue, serializer, options = {}) {
        var _a, _b;
        this.dispatcher = dispatcher;
        this.transport = transport;
        this.queue = queue;
        this.serializer = serializer;
        this.options = options;
        this.pkField = (_a = options.pkField) !== null && _a !== void 0 ? _a : 'pk';
        this.ensurePkFieldInDeleteEvents = (_b = options.ensurePkFieldInDeleteEvents) !== null && _b !== void 0 ? _b : true;
        if (this.options.buildMultiplexedMessage)
            this.buildMultiplexedMessage = this.options.buildMultiplexedMessage.bind(this);
        if (this.options.buildRequestResponseSelector)
            this.buildRequestResponseSelector = this.options.buildRequestResponseSelector.bind(this);
        if (this.options.buildSubscribeCreateSelector)
            this.buildSubscribeCreateSelector = this.options.buildSubscribeCreateSelector.bind(this);
        if (this.options.buildSubscribeUpdateSelector)
            this.buildSubscribeUpdateSelector = this.options.buildSubscribeUpdateSelector.bind(this);
        if (this.options.buildSubscribeDeleteSelector)
            this.buildSubscribeDeleteSelector = this.options.buildSubscribeDeleteSelector.bind(this);
        if (this.options.buildSubscribePayload)
            this.buildSubscribePayload = this.options.buildSubscribePayload.bind(this);
        if (this.options.buildUnsubscribePayload)
            this.buildUnsubscribePayload = this.options.buildUnsubscribePayload.bind(this);
        this.queue.initialize(this.transport.send, this.transport.isConnected);
        this.subscriptions = {};
    }
    initialize() {
        this.transport.connect();
        this.transport.on('message', this.handleTransportMessage);
        this.transport.on('connect', this.handleTransportConnect);
        this.transport.on('reconnect', this.handleTransportReconnect);
    }
    close(unsubscribe = true) {
        let promise;
        if (unsubscribe) {
            promise = this.unsubscribeAll();
        }
        else {
            promise = Promise.resolve();
        }
        return promise.then(() => { this.transport.disconnect(); });
    }
    list(stream, data = {}, requestId) {
        return this.request(stream, {
            action: 'list',
            data,
        }, requestId);
    }
    create(stream, props, requestId) {
        return this.request(stream, {
            action: 'create',
            data: props,
        }, requestId);
    }
    retrieve(stream, pk, data = {}, requestId) {
        return this.request(stream, {
            action: 'retrieve',
            pk,
            data,
        }, requestId);
    }
    update(stream, pk, props, requestId) {
        return this.request(stream, {
            action: 'update',
            pk,
            data: props,
        }, requestId);
    }
    patch(stream, pk, props, requestId) {
        return this.request(stream, {
            action: 'patch',
            pk,
            data: props,
        }, requestId);
    }
    delete(stream, pk, data = {}, requestId) {
        return this.request(stream, {
            action: 'delete',
            pk,
            data,
        }, requestId);
    }
    subscribe(stream, args, callback, options) {
        var _a, _b, _c, _d;
        if (callback == null) {
            throw new Error('callback must be provided');
        }
        if (typeof options === 'string') {
            options = {
                requestId: options
            };
        }
        options = options !== null && options !== void 0 ? options : {};
        options.includeCreateEvents = (_a = options.includeCreateEvents) !== null && _a !== void 0 ? _a : false;
        options.requestId = (_b = options.requestId) !== null && _b !== void 0 ? _b : UUID_1.default.generate();
        options.subscribeAction = (_c = options.subscribeAction) !== null && _c !== void 0 ? _c : 'subscribe_instance';
        options.unsubscribeAction = (_d = options.unsubscribeAction) !== null && _d !== void 0 ? _d : 'unsubscribe_instance';
        if (args !== null && typeof args !== 'object') {
            const pk = args;
            args = {
                [options.subscribeAction === 'subscribe_instance' ? 'pk' : this.pkField]: pk
            };
        }
        const requestId = options.requestId;
        const createSelector = this.buildSubscribeCreateSelector(stream, requestId);
        const updateSelector = this.buildSubscribeUpdateSelector(stream, requestId);
        const deleteSelector = this.buildSubscribeDeleteSelector(stream, requestId);
        const handler = this.buildSubscribeListener(callback);
        const subscribePayload = this.buildSubscribePayload(options.subscribeAction, args, requestId);
        const unsubscribePayload = this.buildUnsubscribePayload(options.unsubscribeAction, args, requestId);
        const subscribeMessage = this.buildMultiplexedMessage(stream, subscribePayload);
        const unsubscribeMessage = this.buildMultiplexedMessage(stream, unsubscribePayload);
        const listenerIds = [];
        const addListener = (selector) => {
            const listenerId = this.dispatcher.listen(selector, handler);
            listenerIds.push(listenerId);
            this.subscriptions[listenerId] = { selector, handler, subscribeMessage, unsubscribeMessage };
        };
        addListener(updateSelector);
        addListener(deleteSelector);
        if (options.includeCreateEvents) {
            addListener(createSelector);
        }
        const requestPromise = this.request(stream, subscribePayload, requestId);
        const unsubscribe = () => __awaiter(this, void 0, void 0, function* () {
            const wasSubbed = listenerIds.map(this.unsubscribe).some(Boolean);
            yield this.request(stream, unsubscribePayload, requestId);
            return wasSubbed;
        });
        return new subscriptions_1.SubscriptionPromise((resolve, reject) => {
            requestPromise.then(resolve, reject);
        }, unsubscribe);
    }
    unsubscribeAll() {
        const subscriptions = Object.values(this.subscriptions);
        const unsubscribeMessages = lodash_uniqby_1.default(subscriptions, s => {
            var _a, _b;
            // @ts-ignore
            return (_b = (_a = s.unsubscribeMessage) === null || _a === void 0 ? void 0 : _a.payload) === null || _b === void 0 ? void 0 : _b.request_id;
        });
        const listenerIds = Object.keys(this.subscriptions).map(parseInt);
        log.info('Removing %d listeners', listenerIds.length);
        listenerIds.forEach(listenerId => this._unsubscribeUnsafe(listenerId));
        log.info('Sending %d unsubscription requests', unsubscribeMessages.length);
        const unsubscriptionPromises = [];
        for (const { unsubscribeMessage } of unsubscribeMessages) {
            const { stream } = unsubscribeMessage, _a = unsubscribeMessage.payload, { request_id } = _a, payload = __rest(_a, ["request_id"]);
            unsubscriptionPromises.push(this.request(stream, payload, request_id).catch(() => { }));
        }
        return Promise.all(unsubscriptionPromises).then(() => listenerIds.length);
    }
    /**
     * Send subscription requests for all registered subscriptions
     */
    resubscribe() {
        const subscriptions = Object.values(this.subscriptions);
        const resubscribeMessages = lodash_uniqby_1.default(subscriptions, s => {
            var _a, _b;
            // @ts-ignore
            return (_b = (_a = s.subscribeMessage) === null || _a === void 0 ? void 0 : _a.payload) === null || _b === void 0 ? void 0 : _b.request_id;
        });
        log.info('Resending %d subscription requests', subscriptions.length);
        for (const { subscribeMessage } of resubscribeMessages) {
            this.sendNow(subscribeMessage);
        }
    }
    sendRequest(payload, requestId, stream) {
        payload = Object.assign({}, payload, { request_id: requestId });
        if (this.options.preprocessPayload != null) {
            // Note: this and the preprocessMessage handler below presume an object will be returned.
            //       If you really want to return a 0, you're kinda SOL -- wrap it in an object :P
            payload = this.options.preprocessPayload(stream, payload, requestId) || payload;
        }
        let message = this.buildMultiplexedMessage(stream, payload);
        if (this.options.preprocessMessage != null) {
            message = this.options.preprocessMessage(message) || message;
        }
        this.send(message);
    }
    request(stream, payload, requestId = UUID_1.default.generate()) {
        return new Promise((resolve, reject) => {
            const selector = this.buildRequestResponseSelector(stream, requestId);
            this.dispatcher.once(selector, (data) => {
                const { payload: response } = data;
                const responseStatus = response.response_status;
                // 2xx is success
                if (Math.floor(responseStatus / 100) === 2) {
                    resolve(response.data);
                }
                else {
                    reject(response);
                }
            });
            this.sendRequest(payload, requestId, stream);
        });
    }
    streamingRequest(stream, payload, callback, requestId = UUID_1.default.generate()) {
        const selector = this.buildRequestResponseSelector(stream, requestId);
        let cancelable;
        let listenerId = this.dispatcher.listen(selector, (data) => {
            const { payload: response } = data;
            const responseStatus = response.response_status;
            if (!cancelable.listenerId) {
                // we promise not to call callback after cancel.
                return;
            }
            // 2xx is success
            if (Math.floor(responseStatus / 100) === 2) {
                callback(null, response.data);
            }
            else {
                cancelable.cancel().finally(() => {
                    callback(response, null);
                });
            }
        });
        cancelable = new StreamingRequestPromise((resolve, reject) => {
            try {
                this.sendRequest(payload, requestId, stream);
                resolve();
            }
            catch (e) {
                reject(e);
            }
        }, this.dispatcher, listenerId);
        return cancelable;
    }
    send(object) {
        return this.doSend(object, this.queue.send);
    }
    sendNow(object) {
        return this.doSend(object, this.queue.sendNow);
    }
    doSend(object, send) {
        const bytes = this.serializer.serialize(object);
        return send(bytes);
    }
    unsubscribe(listenerId) {
        if (this.subscriptions.hasOwnProperty(listenerId)) {
            this._unsubscribeUnsafe(listenerId);
            return true;
        }
        else {
            return false;
        }
    }
    _unsubscribeUnsafe(listenerId) {
        this.dispatcher.cancel(listenerId);
        delete this.subscriptions[listenerId];
    }
    handleTransportMessage(event) {
        log.debug('Received message over transport: %s', event.data);
        const data = this.serializer.deserialize(event.data);
        return this.handleMessage(data);
    }
    handleMessage(data) {
        this.dispatcher.dispatch(data);
    }
    handleTransportConnect() {
        log.debug('Initial API connection over transport %s', this.transport.constructor.name);
        this.queue.processQueue();
    }
    handleTransportReconnect() {
        log.debug('Reestablished API connection');
        this.resubscribe();
        this.queue.processQueue();
    }
    buildMultiplexedMessage(stream, payload) {
        return { stream, payload };
    }
    buildRequestResponseSelector(stream, requestId) {
        return {
            stream,
            payload: { request_id: requestId },
        };
    }
    buildSubscribeCreateSelector(stream, requestId) {
        return {
            stream,
            payload: {
                action: 'create',
                request_id: requestId,
            },
        };
    }
    buildSubscribeUpdateSelector(stream, requestId) {
        return {
            stream,
            payload: {
                action: 'update',
                request_id: requestId,
            },
        };
    }
    buildSubscribeDeleteSelector(stream, requestId) {
        return {
            stream,
            payload: {
                action: 'delete',
                request_id: requestId,
            },
        };
    }
    buildSubscribePayload(action, args, requestId) {
        return Object.assign({ action, request_id: requestId }, args);
    }
    buildUnsubscribePayload(action, args, requestId) {
        return Object.assign({ action, request_id: requestId }, args);
    }
    /**
     * Build a function which will take an entire JSON message and return only
     * the relevant payload (usually an object).
     */
    buildListener(callback) {
        return (data) => {
            return callback(data.payload.data, data);
        };
    }
    /**
     * Build a function which will take an entire JSON message and return only
     * the relevant payload (usually an object).
     */
    buildSubscribeListener(callback) {
        return this.buildListener((data, response) => {
            const action = response.payload.action;
            if (action === 'delete'
                && this.ensurePkFieldInDeleteEvents
                && !data.hasOwnProperty(this.pkField)) {
                // Ensure our configured pkField is used to house primary key
                data[this.pkField] = data.pk;
                // And clear out `pk`
                delete data.pk;
            }
            return callback(data, action);
        });
    }
}
__decorate([
    autobind_decorator_1.default
], DCRFClient.prototype, "unsubscribe", null);
__decorate([
    autobind_decorator_1.default
], DCRFClient.prototype, "handleTransportMessage", null);
__decorate([
    autobind_decorator_1.default
], DCRFClient.prototype, "handleTransportConnect", null);
__decorate([
    autobind_decorator_1.default
], DCRFClient.prototype, "handleTransportReconnect", null);
exports.DCRFClient = DCRFClient;
exports.default = {
    /**
     * Configure a DCRFClient client and begin connection
     *
     * @param url WebSocket URL to connect to
     * @param options Configuration for DCRFClient and ReconnectingWebsocket
     */
    connect(url, options = {}) {
        const client = this.createClient(url, options);
        client.initialize();
        return client;
    },
    /**
     * Create a configured DCRFClient client instance, using default components
     *
     * @param url WebSocket URL to connect to
     * @param options Configuration for DCRFClient and ReconnectingWebsocket
     */
    createClient(url, options = {}) {
        const dispatcher = options.dispatcher || new fifo_1.default();
        const transport = options.transport || new websocket_1.default(url, options.websocket);
        const queue = options.queue || new fifo_2.default();
        const serializer = options.serializer || new json_1.default();
        return new DCRFClient(dispatcher, transport, queue, serializer, options);
    },
};
